// src/app/pages/cost-metrics/costing-data/costing-data.component.ts
import { Component, ChangeDetectionStrategy, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { CostingDataStore, CostGroupKey } from '../../../core/shared/costing-data.store';

type CostItem = { id: string; label: string; value: number };

@Component({
    selector: 'app-costing-data',
    standalone: true,
    imports: [FormsModule, SelectModule, InputTextModule],
    templateUrl: './costing-data.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CostingDataComponent {
    private store = inject(CostingDataStore);

    groups = [
        { label: 'Health Sector Cost', value: 'Health Sector Cost' as CostGroupKey },
        { label: 'Awareness Raising', value: 'Awareness raising' as CostGroupKey },
        { label: 'Screening corner', value: 'Screening Corner' as CostGroupKey },
        { label: 'Active search', value: 'Active search' as CostGroupKey },
        { label: 'Caring for all protocol', value: 'Caring for all protocol' as CostGroupKey },
        { label: 'Remote training', value: 'Remote training' as CostGroupKey },
        { label: 'Risk satisfaction tool', value: 'Risk stratification tool' as CostGroupKey },
        { label: 'PAP card', value: 'PAP card' as CostGroupKey },
        { label: 'Adherence combo', value: 'Adherence combo' as CostGroupKey },
        { label: 'Governence modal and Management panel', value: 'Management panel' as CostGroupKey }
        // Optional additional groups if needed:
        // { label: 'Social Cost', value: 'Social Cost' as CostGroupKey },
        // { label: 'QALYs Calculation', value: 'QALYs Calculation' as CostGroupKey },
    ];
    group: CostGroupKey = 'Health Sector Cost';

    private presets: Record<CostGroupKey, CostItem[]> = {
        'Health Sector Cost': [
            { id: '1', label: 'Discount rate', value: 0.03 },
            { id: '2', label: 'Diagnosis cost', value: 38 },
            { id: '3', label: 'Annual hypertension treatment cost', value: 199 },
            { id: '4', label: 'Annual CVD treatment cost', value: 200 },
            { id: '5', label: 'Stroke event cost', value: 5864 },
            { id: '6', label: 'CHD event cost', value: 1100 }
        ],
        'Awareness raising': [
            { id: '1', label: 'Development of the curriculum', value: 5000 },
            { id: '2', label: 'Printing training and advocacy materials', value: 500 },
            { id: '3', label: 'Salary and transport', value: 300 },
            { id: '4', label: 'Training heart friends and ambassadors', value: 500 },
            { id: '5', label: 'Communication cost', value: 200 }
        ],
        'Screening Corner': [
            { id: '1', label: 'Screening corner equipment', value: 485 },
            { id: '2', label: 'Advocacy materials, production and delivery', value: 109 },
            { id: '3', label: 'Health facility personnel', value: 3258 },
            { id: '4', label: 'Maintenance cost for SC', value: 100 }
        ],
        'Active search': [
            { id: '1', label: 'BP measuring device', value: 997 },
            { id: '2', label: 'Salaries per outreach activities', value: 102 },
            { id: '3', label: 'Transport for outreach activity', value: 5 },
            { id: '4', label: 'Communication cost', value: 200 }
        ],
        'Caring for all protocol': [
            { id: '1', label: 'Development of the protocol', value: 5000 },
            { id: '2', label: 'Printing training and advocacy materials protocol', value: 11 },
            { id: '3', label: 'Training caring for all protocol for health staff', value: 88 },
            { id: '4', label: 'Refresher training every year', value: 118 },
            { id: '5', label: 'Communication cost', value: 200 }
        ],
        'Remote training': [
            { id: '1', label: 'Development of the curriculum remote training', value: 5000 },
            { id: '2', label: 'Printing training and advocacy materials training', value: 50 },
            { id: '3', label: 'Refresher training every remote training', value: 118 },
            { id: '4', label: 'Communication cost', value: 200 }
        ],
        'Risk stratification tool': [
            { id: '1', label: 'Development of the risk stratification tool', value: 5000 },
            { id: '2', label: 'Printing training and advocacy material tool', value: 50 },
            { id: '3', label: 'Refresher training city risk stratification tool', value: 118 },
            { id: '4', label: 'Printing material tool', value: 44 },
            { id: '5', label: 'Communication cost', value: 200 }
        ],
        'PAP card': [
            { id: '1', label: 'Development of the PAP card', value: 5000 },
            { id: '2', label: 'Initial training to health facilities personnel', value: 48 },
            { id: '3', label: 'Printing and production of all materials', value: 11 },
            { id: '4', label: 'Communication cost', value: 200 },
            { id: '5', label: 'Further printing of materials', value: 44 }
        ],
        'Adherence combo': [
            { id: '1', label: 'Development of the adherence combo package', value: 5000 },
            { id: '2', label: 'Printing training and advocacy material combo', value: 80 },
            { id: '3', label: 'Training adherence combo to health proffesionals', value: 133 },
            { id: '4', label: 'Refresher training every year combo', value: 118 },
            { id: '5', label: 'Further printing of adherence forms', value: 44 },
            { id: '6', label: 'Communication cost', value: 200 }
        ],
        'Governance model': [{ id: '1', label: 'Salary for governance role', value: 118 }],
        'Management panel': [{ id: '1', label: 'Salary manegerial panel utilisation', value: 527 }],
        // Optional groups keep if needed
        'Social Cost': [
            { id: '1', label: 'Cost of productivity loss per CVD event', value: 1 },
            { id: '2', label: 'Cost of funeral', value: 25 }
        ],
        'QALYs Calculation': [
            { id: '1', label: 'Brazil GDP per capita 2024', value: 10837 },
            { id: '2', label: 'GDP per capita', value: 2 },
            { id: '3', label: 'utility uncontrolled', value: 0.84 },
            { id: '4', label: 'utility controlled', value: 0.89 },
            { id: '5', label: 'utility cvd events', value: 0.033 },
            { id: '6', label: 'mutility cvd patients', value: 0.6 }
        ]
    };

    get items(): CostItem[] {
        const arr = this.presets[this.group] ?? [];
        // Publish current group values to the store
        const values: Record<string, number> = {};
        arr.forEach((i) => (values[i.label] = i.value));
        this.store.setGroupValues(this.group, values);
        return arr;
    }

    format(n: number): string {
        return new Intl.NumberFormat('en-IN').format(n);
    }
    parseInput(v: string): number {
        const num = Number(String(v).replace(/[, ]+/g, ''));
        return Number.isFinite(num) ? num : 0;
    }

    onEdit(id: string, text: string) {
        const arr = this.presets[this.group];
        const idx = arr.findIndex((i) => i.id === id);
        if (idx >= 0) {
            arr[idx] = { ...arr[idx], value: this.parseInput(text) };
            // Update store for this group after edit
            const values: Record<string, number> = {};
            arr.forEach((i) => (values[i.label] = i.value));
            this.store.setGroupValues(this.group, values);
        }
    }

    trackById(_index: number, item: { id: string }) {
        return item.id;
    }
}
